/**
 * Wer im Rechnungsmodul welchen Knopf bedienen darf (Rollensystem,
 * docs/rollensystem-plan.md §4/§11).
 *
 * Eine Stelle für Belegliste, Beleg und Dialoge — rein und ohne React, damit
 * die Regeln ohne DOM prüfbar sind. Die Ansicht selbst (`rechnungen.lesen`)
 * sperrt App.tsx; hier geht es um das, was man darin TUN darf.
 *
 * Fehlt ein Recht, bleibt der Knopf sichtbar, ist aber gesperrt und nennt im
 * Titel das fehlende Recht („Recht „Rechnungen ausstellen“ fehlt“). Wer die
 * Rechnungen lesen darf, sieht so, dass es den Vorgang gibt und wen er fragen
 * muss — ein verschwundener Knopf wird lange gesucht (dieselbe Überlegung wie
 * am gesperrten Storno-Knopf), und die Leisten springen nicht je nach Rolle.
 *
 * Die Regeln folgen dem Rechte-Trigger auf `invoices` (Nr. 151,
 * `invoices_recht_pruefen`): neue Belege und Entwurfsänderungen verlangen
 * `bearbeiten`, jeder Wechsel aus den Entwurfsstufen heraus (Ausstellen,
 * Storno, Gutschrift, Mahnung) `ausstellen`. Der Client erlaubt nie mehr als
 * die Datenbank — wo ein Vorgang beides schreibt, verlangt der Knopf beides.
 *
 * Admins haben jedes Recht: für sie ist jeder Knopf frei wie bisher. Ohne
 * geladene Rechte (Migration offen) hat ein Nicht-Admin keines — er kommt
 * dann gar nicht erst in die Ansicht.
 *
 * Ausnahme Buchhaltungsansicht: sie hat keine einzelnen Rechte je Knopf,
 * sondern einen Lesemodus (`buchhaltungAktionen`) — dort fehlt immer
 * dasselbe Recht.
 */
import type { RechtSchluessel } from '../../services/rechte/katalog';
import type { RechteSicht } from '../../services/rechte/rechteService';
import { rechtFehltHinweis } from '../../services/rechte/rechtFehler';

/** Zustand eines Knopfs nach Rechten. */
export interface KnopfRecht {
    erlaubt: boolean;
    /** Titel am gesperrten Knopf; fehlt, wenn er erlaubt ist. */
    hinweis?: string;
}

type Rechte = Pick<RechteSicht, 'hat'>;

/** Erlaubt, wenn ALLE Rechte da sind; sonst nennt der Hinweis das erste fehlende. */
export const knopfRecht = (rechte: Rechte, ...benoetigt: RechtSchluessel[]): KnopfRecht => {
    const fehlt = benoetigt.find(r => !rechte.hat(r));
    return fehlt ? { erlaubt: false, hinweis: rechtFehltHinweis(fehlt) } : { erlaubt: true };
};

/**
 * Der Reiter „Buchhaltung“ (AdminAccountingView) und der Sprung dorthin aus
 * dem Projekt: offen mit `rechnungen.lesen` ODER `rechnungen.bearbeiten`.
 *
 * Wer nur liest, bekommt die Ansicht im Lesemodus (`buchhaltungNurLesen`).
 * Das ist hier tragend und keine Höflichkeit: die Ansicht schreibt direkt in
 * den Projektstand — Verzicht, manuelle Verbuchung, Rücknahme von Buchungen,
 * Alt-Abschläge, Aufteilungsschlüssel —, und den schützt keine
 * Datenbankregel (Plan §11, `projects` bleibt offen). Ohne bearbeiten sperrt
 * deshalb der Client jeden dieser Wege.
 *
 * Fehlt beides, nennt der Hinweis das Leserecht — das ist das, was man für
 * die Ansicht braucht.
 */
export const buchhaltungRecht = (rechte: Rechte): KnopfRecht =>
    rechte.hat('rechnungen.lesen') || rechte.hat('rechnungen.bearbeiten')
        ? { erlaubt: true }
        : { erlaubt: false, hinweis: rechtFehltHinweis('rechnungen.lesen') };

/**
 * Zugang zur Buchhaltung, aber ohne `rechnungen.bearbeiten`: die Ansicht
 * öffnet im Lesemodus. Ohne Zugang false — dann kommt man gar nicht hinein.
 */
export const buchhaltungNurLesen = (rechte: Rechte): boolean =>
    buchhaltungRecht(rechte).erlaubt && !rechte.hat('rechnungen.bearbeiten');

/** Titel an jedem gesperrten Schreibweg der Buchhaltung im Lesemodus. */
export const BUCHHALTUNG_NUR_LESEN_HINWEIS = `Nur lesen — ${rechtFehltHinweis('rechnungen.bearbeiten')}`;

/**
 * Jeder Schreibweg der Buchhaltungsansicht und der Dialoge, die sie öffnet.
 * Lesen, Filtern, Springen und PDFs ansehen stehen nicht darin — die bleiben
 * im Lesemodus frei.
 */
export interface BuchhaltungAktionen {
    /** „Nicht mehr abrechnen“ / „Verzicht bearbeiten“ (ServiceWaiverDialog). */
    verzicht: KnopfRecht;
    /** „Als abgerechnet buchen“ samt Rücknahme (ManualBillingDialog). */
    manuelleVerbuchung: KnopfRecht;
    /** „Buchung zurücknehmen“ (ServiceReversalDialog). */
    ruecknahme: KnopfRecht;
    /** ALT-Buchungen auf eine andere Position umhängen. */
    zuordnungKorrigieren: KnopfRecht;
    /**
     * Vorrechnungen (ExternalAdvancesPanel): Abschlag nachtragen, ändern,
     * löschen und „Letzte Rechnung einlesen“ — das schreibt Abschläge UND
     * nachgetragene Leistung ins Projekt.
     */
    vorrechnungen: KnopfRecht;
    /**
     * Aufteilungsschlüssel ändern, aufheben und den Altbestand zuordnen. Den
     * Schlüssel ANSEHEN darf man weiter.
     */
    aufteilungsschluessel: KnopfRecht;
    /** Umstellungs-Assistent Monatspauschale (FlatRateSwitchAssistant). */
    monatspauschale: KnopfRecht;
    /** „Rechnung erstellen“ — öffnet den Anlagedialog eines neuen Belegs. */
    rechnungErstellen: KnopfRecht;
    /** Altbuchung vor dem Rechnungsmodul zurücknehmen (Archiv, „Wiederherstellen“). */
    altbuchungStornieren: KnopfRecht;
    /** Archivierten Bericht aus der Berichtshistorie löschen. */
    archivLoeschen: KnopfRecht;
    /**
     * Leistungsnachweis: neue Einträge, die der Export ins Projekt schreibt.
     * Das PDF selbst — auch mit Änderungen nur für das PDF — bleibt.
     */
    nachweisEintraege: KnopfRecht;
}

/** Im Lesemodus ist jeder Schreibweg gesperrt, sonst jeder frei wie bisher. */
export const buchhaltungAktionen = (nurLesen: boolean): BuchhaltungAktionen => {
    const k: KnopfRecht = nurLesen ? { erlaubt: false, hinweis: BUCHHALTUNG_NUR_LESEN_HINWEIS } : { erlaubt: true };
    return {
        verzicht: k,
        manuelleVerbuchung: k,
        ruecknahme: k,
        zuordnungKorrigieren: k,
        vorrechnungen: k,
        aufteilungsschluessel: k,
        monatspauschale: k,
        rechnungErstellen: k,
        altbuchungStornieren: k,
        archivLoeschen: k,
        nachweisEintraege: k,
    };
};

export interface RechnungAktionen {
    // ── Belegliste ──────────────────────────────────────────────────────
    /** Neue Rechnung (Anlagedialog samt Nachtrag undatierter Mengen). */
    neueRechnung: KnopfRecht;
    rechnungslauf: KnopfRecht;
    /** Steuerberater-Export; setzt `exported_at` an den Belegen. */
    datevExport: KnopfRecht;
    /** Kontoauszug einlesen und Zahlungen zuordnen. */
    bankImport: KnopfRecht;
    /**
     * Zahlung erfassen oder löschen, Skonto, Einbehalt, Kundenguthaben aus
     * Überzahlung — Liste wie Beleg.
     */
    zahlungen: KnopfRecht;
    /**
     * Mahnstufe vermerken. Schreibt `dunning_level` an einem ausgestellten
     * Beleg — für die Datenbank ein Wechsel außerhalb der Entwurfsstufen,
     * also `ausstellen`.
     */
    mahnen: KnopfRecht;
    /** Reiter „Buchhaltung“; ohne bearbeiten im Lesemodus (`buchhaltungNurLesen`). */
    buchhaltung: KnopfRecht;
    // ── Beleg ───────────────────────────────────────────────────────────
    /**
     * Entwurf ändern: Felder, Autosave, Vorbelegungen, Neuaufbau der
     * Positionen, Nachweis-Upload, Entwürfe löschen, Projektvorgabe des
     * Einbehalts und Alt-Einbehalte.
     */
    bearbeiten: KnopfRecht;
    zurPruefung: KnopfRecht;
    /**
     * Freigeben ist die Vorstufe des Ausstellens: wer nicht ausstellen darf,
     * gibt nicht frei. Geschrieben wird dabei ein Entwurfsstatus — dafür
     * verlangt die Datenbank `bearbeiten`, also beide.
     */
    freigeben: KnopfRecht;
    zurueckweisen: KnopfRecht;
    ausstellen: KnopfRecht;
    /**
     * Korrekturrechnung und kaufmännische Gutschrift. Beide legen einen NEUEN
     * Beleg an (`bearbeiten`) und stellen ihn aus (`ausstellen`).
     */
    korrigieren: KnopfRecht;
    /** Wie Korrigieren: der Stornobeleg entsteht als neuer Beleg. */
    stornieren: KnopfRecht;
    // ── Kunden ──────────────────────────────────────────────────────────
    /**
     * Beim Anlegen einem Projekt ohne Kunden einen zuordnen. Schreibt nur
     * `projects.customer_id`, nicht den Kundenstamm — ein Schritt der
     * Rechnungsanlage, deshalb `bearbeiten`.
     */
    kundeZuordnen: KnopfRecht;
    /**
     * Kundenstamm aus dem Beleg heraus ändern (`customers`, Recht „Kunden
     * verwalten“). Ohne bleibt im offenen Entwurf die belegbezogene
     * Abweichung — die schreibt am Beleg und hängt an `bearbeiten`.
     */
    kundenstamm: KnopfRecht;
}

export const rechnungAktionen = (rechte: Rechte): RechnungAktionen => {
    const bearbeiten = knopfRecht(rechte, 'rechnungen.bearbeiten');
    const ausstellen = knopfRecht(rechte, 'rechnungen.ausstellen');
    const zahlungen = knopfRecht(rechte, 'zahlungen.buchen');
    const neuerBelegAusgestellt = knopfRecht(rechte, 'rechnungen.bearbeiten', 'rechnungen.ausstellen');
    return {
        neueRechnung: bearbeiten,
        rechnungslauf: bearbeiten,
        datevExport: knopfRecht(rechte, 'datev.export'),
        bankImport: zahlungen,
        zahlungen,
        mahnen: ausstellen,
        buchhaltung: buchhaltungRecht(rechte),
        bearbeiten,
        zurPruefung: bearbeiten,
        freigeben: knopfRecht(rechte, 'rechnungen.bearbeiten', 'rechnungen.ausstellen'),
        zurueckweisen: bearbeiten,
        ausstellen,
        korrigieren: neuerBelegAusgestellt,
        stornieren: neuerBelegAusgestellt,
        kundeZuordnen: bearbeiten,
        kundenstamm: knopfRecht(rechte, 'kunden.verwalten'),
    };
};
